/**
 * SafetyDashboardView — shared shell for the framework-based safety dashboards
 * (Safety Overview + unified Home). Wires site/time/dimension filters, saved
 * views, export, and the customizable widget grid. Callers vary only the
 * dashboardKey (→ separate saved layouts), title, defaultLayout, and header.
 */
import React, { useState } from 'react';
import TabNavigation from '../../components/TabNavigation';
import { useSite } from '../../context/SiteContext';
import {
  CustomizableDashboard, FilterBar, SavedFilters, ExportMenu, DimensionFilters,
  StandardToggle, getStoredStandard, useTimeFilter, useDimensionFilters,
} from '@/dashboard';
import { SAFETY_DIMENSIONS, safetyExportSpecForWidgets, safetyCatalogFor, safetyDashboardAccess } from '@/dashboard/presets/safety';
import { usePermissions } from '../../context/PermissionContext';

const DIMENSION_KEYS = SAFETY_DIMENSIONS.map((d) => d.key);

export default function SafetyDashboardView({
  user, onLogout, dashboardKey, title, subtitle, defaultLayout, headerExtra = null,
  // Replaces the default title block entirely. A landing page wants a greeting
  // and quick actions where an analytics page wants a heading; rather than
  // grow one header with flags for both, a caller that needs its own supplies
  // it. Callers that don't pass it are unaffected.
  header = null,
}) {
  const { activeSiteId } = useSite();
  const { hasPermission } = usePermissions();
  // Permission-scoped widget catalog: `analytics:read` gets analytics, others
  // get the personal set. Customize stays open to everyone — it only moves a
  // user's own widgets, and the catalog is what limits which widgets exist.
  const access = React.useMemo(
    () => safetyDashboardAccess(hasPermission, user?.role), [hasPermission, user?.role],
  );
  const isManagement = access.analytics;
  const CATALOG = React.useMemo(() => safetyCatalogFor(access), [access]);
  // Prefix URL filter params by dashboardKey so two safety dashboards don't
  // clash if their filters are ever deep-linked.
  const { preset, dateFrom, dateTo, time, setFilter } = useTimeFilter({ paramPrefix: `${dashboardKey}_` });
  const { selected: dimensions, setDimension, clearAll } = useDimensionFilters({
    dimensions: DIMENSION_KEYS, paramPrefix: `${dashboardKey}_dim_`,
  });
  // Frequency-rate reporting standard (OSHA / Indian) — per-user preference.
  const [standard, setStandard] = useState(getStoredStandard);

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {header || (
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
              {subtitle && <p className="text-gray-600 mt-1">{subtitle}</p>}
            </div>
            {headerExtra}
          </div>
        )}

        <CustomizableDashboard
          dashboardKey={dashboardKey}
          module="safety"
          siteId={activeSiteId}
          time={time}
          dimensions={dimensions}
          compare
          standard={standard}
          catalog={CATALOG}
          defaultLayout={defaultLayout}
          filterBar={isManagement ? (
            <FilterBar
              preset={preset}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChange={setFilter}
              extra={
                <DimensionFilters
                  module="safety"
                  dims={SAFETY_DIMENSIONS}
                  selected={dimensions}
                  onChange={setDimension}
                  onClearAll={clearAll}
                />
              }
            />
          ) : null}
          actions={isManagement ? (
            <>
              <SavedFilters
                module="safety"
                current={{ time }}
                onApply={(f) => {
                  const t = f?.time;
                  if (t?.preset) setFilter({ preset: t.preset, dateFrom: t.date_from, dateTo: t.date_to });
                }}
              />
              <StandardToggle value={standard} onChange={setStandard} />
              {access.exportReports && (
                <ExportMenu module="safety" specBuilder={safetyExportSpecForWidgets} />
              )}
            </>
          ) : null}
        />
      </div>
    </div>
  );
}
